import React, { useCallback, useContext, useMemo, useState } from 'react';

import {
  TemplateQuestion,
  TemplateQuestionType,
  useFetchTemplateQuestions
} from '~/components/Shared/hooks/useFetchTemplateQuestions';
import { useUpsertDraftCampaign } from '~/components/Shared/hooks/useMutateCampaignDetails';
import {
  decorateTemplateQuestion,
  EnhancedTemplateQuestion,
  evaluateQuestionMeta,
  getCustomKPI,
  getQuestionMeta,
  shouldUseQuestionMeta,
  resetCustomKPI,
  validateConventionalKPIs,
  validateCustomKPIs
} from '~/utils/templateQuestions';

import { useCampaignManager } from '../../CampaignManagerProvider';

export enum QuestionMoveDir {
  'up' = 'up',
  'down' = 'down'
}

type SubmitProp = {
  onSuccess: () => void;
};

type StandardQuestionsProvider = {
  standardTemplateQuestions: EnhancedTemplateQuestion[];
  standardQuestions: EnhancedTemplateQuestion[];
  hasPendingQuestions: boolean;
  addStandardQuestion: (id: string) => void;
  submitStandardQuestions: (prop: SubmitProp) => void;
  setPrimaryQuestion: (index: number) => void;
  deleteStandardQuestion: (index: number) => void;
  moveStandardQuestion: (index: number, dir: QuestionMoveDir) => void;
  saveStandardQuestion: (
    index: number,
    data: Partial<TemplateQuestion>
  ) => void;
  customKPIErrors: Record<string, any>;
  hasCustomKPIErrors: boolean;
  kpiErrors: Record<string, any>;
  hasKPIErrors: boolean;
  translatedNOTAValue: string;
};

type StandardQuestionsProviderProps = {
  children: React.ReactNode;
};

const AD_RECALL = 'ad_recall';

const StandardQuestionsContext = React.createContext<StandardQuestionsProvider>(
  {} as StandardQuestionsProvider
);

export const useStandardQuestionsProvider = () =>
  useContext<StandardQuestionsProvider>(StandardQuestionsContext);

export const StandardQuestionsProvider: React.FC<StandardQuestionsProviderProps> = ({
  children
}) => {
  const [hasPendingQuestions, setPendingQuestions] = useState<boolean>(false);
  const {
    managedCampaign,
    refetchCampaign,
    onMutateError,
    can
  } = useCampaignManager();

  // NOTE: this value is needed in order to not have an empty object as question_meta
  const initialQuestionMeta = useMemo(() => {
    return getQuestionMeta(managedCampaign);
  }, [managedCampaign?.details?.draft_settings?.question_meta]);

  const [questionMeta, setQuestionMeta] = useState<{ [key: string]: any }>(
    initialQuestionMeta
  );

  const questions = managedCampaign?.details.draft_settings.questions;

  const { mutate: mutateCampaign } = useUpsertDraftCampaign(
    managedCampaign?.details?.campaign_id
  );

  const { data } = useFetchTemplateQuestions({
    type: TemplateQuestionType.Lift,
    campaignId: managedCampaign?.details.campaign_id,
    isEnabled: true
  });

  // Current campaign custom questions duplicated from templates
  const [standardQuestions, setStandardQuestions] = useState<
    EnhancedTemplateQuestion[]
  >(
    questions
      ? (questions.map(decorateTemplateQuestion) as EnhancedTemplateQuestion[])
      : []
  );

  // Default questions that should be duplicated and possibly customized
  const standardTemplateQuestions = useMemo(() => {
    if (!data?.items) return [];
    const itemsCopy = [...data.items];

    if (can.addCustomKPI) {
      itemsCopy.push(
        getCustomKPI({
          questions: data.items,
          isBehavioralCustomKPI: false
        })
      );
    }

    return itemsCopy.map(decorateTemplateQuestion);
  }, [data, can]);

  const { customKPIErrors, hasCustomKPIErrors } = useMemo(() => {
    return validateCustomKPIs(standardQuestions);
  }, [standardQuestions]);

  const { kpiErrors, hasKPIErrors } = useMemo(() => {
    return validateConventionalKPIs(standardQuestions);
  }, [standardQuestions]);

  const translatedNOTAValue = useMemo(() => {
    if (!standardTemplateQuestions?.length) return '';

    const adRecallQuestion = standardTemplateQuestions.find(
      question => question.kpi_type === AD_RECALL
    );

    const notaValue =
      !!adRecallQuestion && !!adRecallQuestion?.none_of_the_above_answer?.length
        ? adRecallQuestion.none_of_the_above_answer
        : '';

    return notaValue;
  }, [standardQuestions, standardTemplateQuestions]);

  const addStandardQuestion = useCallback(
    (id: string) => {
      const currentQuestion = standardTemplateQuestions.find(
        item => item.kpi_type === id
      );

      if (!currentQuestion) return;

      const sanitizedQuestion = {
        ...currentQuestion,
        // Should always be true when the first question is added
        default: !standardQuestions.length,
        question_version: 2
      };

      // Keep existing order
      const updatedQuestions = [...standardQuestions, sanitizedQuestion];
      const sortedQuestions = [...updatedQuestions].sort(
        (a, b) => a.order - b.order
      );

      setStandardQuestions(sortedQuestions);
      setPendingQuestions(true);
    },
    [setStandardQuestions, standardTemplateQuestions, standardQuestions]
  );

  const setPrimaryQuestion = useCallback(
    (index: number) => {
      const updatedQuestions = standardQuestions.map((q, idxQ) => ({
        ...q,
        default: idxQ === index
      }));

      setStandardQuestions(updatedQuestions);
      setPendingQuestions(true);
    },
    [standardQuestions, setStandardQuestions]
  );

  const deleteStandardQuestion = useCallback(
    (index: number) => {
      const questions = [...standardQuestions];
      const questionToDelete = questions.find((q, iq) => iq === index);

      if (!questionToDelete) return;

      const filteredQuestions = questions.filter((q, iq) => iq !== index);

      if (questionToDelete.default && filteredQuestions.length >= 1) {
        filteredQuestions[0].default = true;
      }

      setStandardQuestions(filteredQuestions);
      setPendingQuestions(true);
    },
    [setStandardQuestions, standardQuestions]
  );

  const moveStandardQuestion = useCallback(
    (index: number, dir: QuestionMoveDir) => {
      const questions = [...standardQuestions];
      const [currentQ] = questions.splice(index, 1);

      const pos = dir === QuestionMoveDir.up ? index - 1 : index + 1;
      questions.splice(pos, 0, currentQ);

      const orderedQuestions = questions.map((q, i) => ({
        ...q,
        order: i + 1
      }));

      setStandardQuestions(orderedQuestions);
      setPendingQuestions(true);
    },
    [setStandardQuestions, standardQuestions]
  );

  const saveStandardQuestion = useCallback(
    (index: number, data: Partial<TemplateQuestion>) => {
      const questions = [...standardQuestions];
      const updatedQuestion = {
        ...questions[index],
        ...data
      };

      const incomingQuestionMeta = evaluateQuestionMeta({
        initialQuestionMeta,
        questionMeta
      });

      const updatedQuestionMeta = { ...incomingQuestionMeta };

      updatedQuestion.use_question_meta = shouldUseQuestionMeta(
        updatedQuestionMeta,
        updatedQuestion
      );
      questions[index] = updatedQuestion;

      setStandardQuestions(questions);
      setQuestionMeta(updatedQuestionMeta);
      setPendingQuestions(true);
    },
    [setStandardQuestions, standardQuestions, questionMeta]
  );

  const submitStandardQuestions = useCallback(
    ({ onSuccess }: SubmitProp) => {
      if (!managedCampaign) return;

      const resetStandardQuestions = resetCustomKPI(standardQuestions);

      // NOTE: this condition exists due to during the mounting phase of this provider
      // questionMeta is initialized as an empty object. This prevents us from overwriting the questionMeta value with an empty object.
      const updatedQuestionMeta = !!Object.keys(questionMeta)?.length
        ? questionMeta
        : initialQuestionMeta;

      mutateCampaign(
        {
          ...managedCampaign?.details,
          draft_settings: {
            ...managedCampaign?.details?.draft_settings,
            questions: resetStandardQuestions,
            question_meta: updatedQuestionMeta
          }
        },
        {
          onSuccess: () => {
            refetchCampaign();
            setPendingQuestions(false);
            setQuestionMeta({});
            onSuccess?.();
          },
          ...onMutateError
        }
      );
    },
    [
      mutateCampaign,
      setPendingQuestions,
      standardQuestions,
      managedCampaign,
      questionMeta,
      onMutateError,
      refetchCampaign
    ]
  );

  const value = useMemo(
    () => ({
      standardTemplateQuestions,
      standardQuestions,
      addStandardQuestion,
      deleteStandardQuestion,
      moveStandardQuestion,
      saveStandardQuestion,
      hasPendingQuestions,
      submitStandardQuestions,
      setPrimaryQuestion,
      customKPIErrors,
      hasCustomKPIErrors,
      kpiErrors,
      hasKPIErrors,
      translatedNOTAValue
    }),
    [
      standardTemplateQuestions,
      standardQuestions,
      addStandardQuestion,
      deleteStandardQuestion,
      moveStandardQuestion,
      saveStandardQuestion,
      hasPendingQuestions,
      submitStandardQuestions,
      setPrimaryQuestion,
      customKPIErrors,
      hasCustomKPIErrors,
      kpiErrors,
      hasKPIErrors,
      translatedNOTAValue
    ]
  );

  return (
    <StandardQuestionsContext.Provider value={value}>
      {children}
    </StandardQuestionsContext.Provider>
  );
};
